/**
 * ActionEntityPreview — the single entry point for rendering the entity
 * preview slot in any v2 action card.
 *
 * Resolves to the rich ContentComponent (e.g. ContactCardContent) from
 * ENTITY_CONTENT_REGISTRY wrapped in CrmClickableContent when possible,
 * and falls back to the generic EntityCard for unknown entity types. A
 * single useCrmObjectSnapshot subscription per instance powers the rich
 * fields (avatar, subtitle, email, badges) so rendering matches the
 * slideout and doesn't require the calling card to thread flatProps.
 *
 * Use this everywhere an action card displays an entity "chip" — Create,
 * Update, Delete, Link (source + target), Merge (source + target),
 * StageAssociation, HiddenEntity. The goal is one rendering approach for
 * the whole proposal surface instead of the three (EntityCard chip /
 * buildEntityContent compact / inline ENTITY_CONTENT_REGISTRY) we had
 * before.
 *
 * Variant styling (destructive / warning) is applied by wrapping this
 * component in ObjectCard bordered, which keeps the click surface on the
 * child content rather than on the outer card.
 */

import {
  type ReactNode,
  useCallback,
  useMemo,
  useSyncExternalStore,
} from 'react';

import { useCrmObjectSnapshot } from '@/lib/crm/hooks';
import { useAssociations } from '@/lib/crm/hooks/useAssociations';
import { CrmClickableContent } from '@/lib/crm/object-cards/CrmClickableContent';
import { ObjectCard } from '@/lib/crm/object-cards/ObjectCard';
import { useCrmPreview } from '@/lib/crm/preview';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';

import type { CrmObject } from '../../types/crm';
import {
  type ActionEntityPreviewProps,
  deriveTargetTypeFromAssociation,
  flatPropKeyForTargetType,
  HYDRATED_TARGET_TYPES,
} from './ActionEntityPreview.helpers';
import {
  ENTITY_CONTENT_REGISTRY,
  extractProperties,
  isSelfBorderedEntityContent,
} from './CreateActionCard.helpers';
import { EntityCard } from './EntityCard';
import { ExcludedOverlay } from './ExcludedOverlay';

export function ActionEntityPreview({
  entityId,
  entityType,
  label,
  enrichedData,
  orgId,
  variant = 'default',
  compact = false,
  nestedAssociations,
  fallbackFlatProps,
  excluded: excludedProp,
  excludedMessage: excludedMessageProp,
  hideTypeBadge = false,
}: ActionEntityPreviewProps) {
  // Compact renders normally only need the label/enrichedData and skip the
  // store fetch — EXCEPT when a CrmPreviewProvider has a live overlay for
  // this entity id (e.g. a checked sibling create action projecting this
  // entity's proposed name). Subscribing to the overlay presence (not just
  // reading it) keeps the chip in sync as the sibling checkbox toggles.
  // useCrmObjectSnapshot itself also subscribes to the same overlay key
  // internally (to merge overlay data into the returned object) — this is
  // an intentional double-subscription from two hooks in the same
  // component tree, not a bug; React's batching prevents cascading
  // re-renders.
  const preview = useCrmPreview();
  const overlaySubscribe = useCallback(
    (cb: () => void) =>
      preview && entityId ? preview.subscribeToOverlay(entityId, cb) : () => {},
    [preview, entityId]
  );
  const overlayGetSnapshot = useCallback(
    () => (preview && entityId ? preview.getOverlay(entityId) : undefined),
    [preview, entityId]
  );
  const overlay = useSyncExternalStore(overlaySubscribe, overlayGetSnapshot);
  const hasOverlay = overlay !== undefined;
  // Two independent reasons to de-emphasise: the entity's own create is
  // unchecked (overlay.excluded), or the caller says the ACTION showing this
  // entity is unchecked (`excluded` prop — e.g. an unchecked association whose
  // target create is still checked). The caller's wording wins for the latter.
  const excluded = overlay?.excluded === true || excludedProp === true;
  const excludedMessage =
    excludedProp && excludedMessageProp
      ? excludedMessageProp
      : `Won't be created – ${label} is unchecked`;

  // The wrapper renders whenever exclusion is possible: inside a preview
  // provider (overlay-driven) or when the caller controls it via `excluded`,
  // so a later toggle de-emphasises in place instead of remounting.
  const canBeExcluded = preview !== null || excludedProp !== undefined;
  const wrapExcluded = (node: ReactNode): ReactNode =>
    canBeExcluded ? (
      <ExcludedOverlay excluded={excluded} message={excludedMessage}>
        {node}
      </ExcludedOverlay>
    ) : (
      node
    );

  // Full task previews own an association-aware useCrmObject request inside
  // TaskPreviewContent. Starting the generic ensureEntity hydration here as
  // well would race a lean batch request against that detail request for the
  // same object. Other registered cards continue to use this shared snapshot.
  const contentOwnsHydration = entityType === 'ahoy_task' && !compact;
  const storeObject = useCrmObjectSnapshot(
    (compact && !hasOverlay) || contentOwnsHydration ? undefined : entityId,
    orgId
  );

  // Resolve the subset of associations whose targets the current card
  // builders actually read (see HYDRATED_TARGET_TYPES). Rendering a
  // proposal row shouldn't turn every peripheral relationship on the
  // source entity into a background CRM fetch.
  //
  // useAssociations is store-first (reads from the centralized store via
  // useStoreObjects) and only fetches via `ensureEntity` for targets the
  // store hasn't yet hydrated. When the source has no associations or
  // the store hasn't loaded yet, it returns an empty result.
  //
  // Note: we deliberately DON'T block the main skeleton on
  // `associationsLoading`. Some association targets (e.g. pipeline stage
  // entities) don't round-trip through the regular fetch endpoint, so
  // waiting for them would trap the card in a skeleton forever. Instead,
  // sub-cards that depend on specific association targets (e.g. the
  // company sub-card on a deal) carry their own "pending" flag and render
  // their own skeleton locally — see `pendingTargetTypes` below and the
  // `company` handling in `buildDealContentProps`.
  const relevantAssociations = useMemo(() => {
    const all = storeObject?.associations ?? [];
    return all.filter(a => {
      const targetType = deriveTargetTypeFromAssociation(
        a.association_type_name
      );
      return targetType !== null && HYDRATED_TARGET_TYPES.has(targetType);
    });
  }, [storeObject?.associations]);

  // ActionEntityPreview genuinely wants the resolved targets hydrated so the
  // company sub-card (and future nested cards) can render industry/headcount
  // beyond just the name. `relevantAssociations` is already filtered to the
  // HYDRATED_TARGET_TYPES set, so there's no fan-out risk here.
  // `sourceEntityTypeName` still suppresses hydration for known-heavy source
  // types (meetings) via SKIP_ENSURE_FOR_SOURCE_TYPES.
  const { associations: resolvedAssocs } = useAssociations(
    orgId,
    relevantAssociations,
    { sourceEntityTypeName: entityType }
  );

  const flatProps = useMemo(() => {
    const base = storeObject?.properties
      ? extractProperties(storeObject.properties)
      : { ...(fallbackFlatProps ?? {}) };

    // Generic association → flatProps injection. For each resolved target,
    // write its display name under the `<target_entity_type>_name` slot so
    // content-component prop builders can surface it (e.g. a resolved
    // `ahoy_company` target populates `ahoy_company_name`, which both
    // ContactCardContent's "Works at" row and DealCardContent's company
    // sub-card read). No per-source-entity-type branching — the target's
    // own type drives the slot.
    for (const resolved of resolvedAssocs) {
      if (!resolved.object) {
        continue;
      }
      const key = flatPropKeyForTargetType(resolved.object.entity_type_name);
      if (base[key]) {
        continue;
      }
      const displayName = getDisplayName(resolved.object, '');
      if (displayName) {
        base[key] = displayName;
      }
    }

    // Backend-supplied `enrichedData.target_object` represents the associated
    // entity for create / link / merge actions (and occasionally the stage
    // for a stage-association action). Project it into flatProps using the
    // same target-type convention so prop builders don't need to distinguish
    // "is this a company?" — if a builder reads `ahoy_company_name`, it's
    // already populated when the target is a company, and silently ignored
    // when it's a stage or other unrelated type.
    const target = enrichedData?.target_object;
    if (target?.entity_type_name && target.label) {
      const key = flatPropKeyForTargetType(target.entity_type_name);
      if (!base[key]) {
        base[key] = target.label;
      }
    }

    return base;
  }, [
    storeObject?.properties,
    resolvedAssocs,
    enrichedData?.target_object,
    fallbackFlatProps,
  ]);

  // Keyed-by-entity-type map of fully-resolved target CrmObjects. Content
  // components that render a nested entity sub-card (e.g. DealCardContent's
  // company card) can read `resolvedTargets['ahoy_company']` and hand its
  // full property set — industry, headcount, city/country — into their own
  // prop builder. This is what lets the nested card populate beyond just
  // the name: the live store entity already has that data (visible in the
  // slideout) — we just had to pass it through.
  const resolvedTargets = useMemo<Record<string, CrmObject>>(() => {
    const map: Record<string, CrmObject> = {};
    for (const resolved of resolvedAssocs) {
      if (resolved.object) {
        // First occurrence wins so the most-linked target (typically the
        // primary company) takes precedence over any later duplicates.
        map[resolved.object.entity_type_name] ??= resolved.object;
      }
    }
    return map;
  }, [resolvedAssocs]);

  // Set of target entity types the source has an association for, but
  // whose CrmObjects haven't yet been resolved into `resolvedTargets`.
  // Nested sub-cards read this to show a local skeleton while their
  // specific target is loading, without blocking the whole main card.
  //
  // Derived from association_type_name via the `<source>_to_<target>`
  // convention (e.g. `ahoy_deal_to_company` → target type `ahoy_company`).
  // Stage associations (and anything ending in `_stage`) are excluded —
  // stages don't round-trip through the normal entity fetch path, so
  // waiting on them would keep a sub-card in the skeleton state forever.
  const pendingTargetTypes = useMemo<Set<string>>(() => {
    const pending = new Set<string>();
    for (const resolved of resolvedAssocs) {
      if (resolved.object) {
        continue;
      }
      const targetType = deriveTargetTypeFromAssociation(
        resolved.association_type_name
      );
      if (targetType) {
        pending.add(targetType);
      }
    }
    return pending;
  }, [resolvedAssocs]);

  // Content that carries its own card border renders unwrapped — like the
  // EntityCard fallback below — and applies the variant treatment itself via
  // the `variant` prop on EntityContentComponentProps. Which registered types
  // are self-bordered (and the orgId condition) is declared once in
  // isSelfBorderedEntityContent; `compact` still goes through the wrapper.
  const selfBordered = isSelfBorderedEntityContent(entityType, orgId);

  const ContentComponent = ENTITY_CONTENT_REGISTRY[entityType];
  if (!ContentComponent) {
    // EntityCard carries its own border + variant styling, so it can't be
    // safely wrapped in a bordered ObjectCard — that produces a double-
    // nested card border. Return EntityCard unwrapped and let its own
    // variant prop apply the destructive / warning treatment.
    return wrapExcluded(
      <EntityCard
        entityTypeName={entityType}
        label={label}
        entityId={entityId}
        variant={variant}
      />
    );
  }

  // No card-level skeleton anymore — per-field loading lives inside each
  // content component (ContactCardContent etc.), which self-hydrate off
  // the CRM store given an entityId. Each field renders its own skeleton
  // until its value is available; fields passed as explicit props win
  // over store values. See the JSDoc on ContactCardContent for the
  // resolution chain.

  const content = (
    <CrmClickableContent entityId={entityId}>
      <ContentComponent
        entityId={entityId}
        orgId={orgId}
        flatProps={flatProps}
        enrichedData={enrichedData}
        compact={compact}
        hideTypeBadge={hideTypeBadge}
        nestedAssociations={nestedAssociations}
        resolvedTargets={resolvedTargets}
        pendingTargetTypes={pendingTargetTypes}
        variant={selfBordered ? variant : undefined}
      />
    </CrmClickableContent>
  );

  // Registered content components (CompactCardLayout / ContactCardContent
  // etc.) don't carry their own card chrome, so wrap in a bordered
  // ObjectCard to apply the variant treatment when requested. Callers
  // previously wrapped ActionEntityPreview in ObjectCard themselves, which
  // double-nested for unregistered (EntityCard) fallbacks above.
  // Self-bordered content (the task preview) is never wrapped — its own
  // card applies the variant treatment via the prop above. Compact chips
  // carry no border of their own, so they keep the wrap.
  if (variant !== 'default' && (!selfBordered || compact)) {
    return wrapExcluded(
      <ObjectCard bordered variant={variant}>
        {content}
      </ObjectCard>
    );
  }
  return wrapExcluded(content);
}
